import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet } from '../api/client.js';
import Sparkline from './Sparkline.jsx';
import { PLATFORM_META, platformName, platformIcon } from '../lib/platforms.js';

/**
 * ProjectStats — the project "Stats" tab (ADR-0009 engagement, surfaced
 * per-project and broken down BY SOCIAL NETWORK).
 *
 * Pulls three read endpoints, all org- + project-scoped:
 *   - /engagement/summary    → window totals + per-platform engagement sums
 *   - /engagement/timeseries → per-platform impressions series (sparklines)
 *   - /engagement/accounts   → per-connected-account follower counts + trend
 *
 * It then renders one card per social network — the union of connected
 * accounts and platforms that have published engagement — so the owner sees
 * followers, reach, and interaction at a glance per channel. Honest empty
 * states distinguish "nothing connected", "nothing published yet", and
 * "published but stats are blocked platform-side" (almost always a missing
 * insights permission or an expired token), pointing to the Connections tab.
 */

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

const fmt = (n) => Number(n || 0).toLocaleString();

const ENG_KEYS = ['total_impressions', 'total_reach', 'total_likes', 'total_saves', 'total_shares', 'total_comments'];

export function ProjectStats({ projectKey }) {
  // Rolling last-N-days chips OR an explicit from/to range (owner request
  // 2026-07-26) — from/to wins when either date is set.
  const [range, setRange] = useState({ days: 30, from: '', to: '' });
  const custom = Boolean(range.from || range.to);
  const days = range.days;
  const enc = encodeURIComponent(projectKey);
  const rangeQs = custom
    ? `${range.from ? `&from=${range.from}` : ''}${range.to ? `&to=${range.to}` : ''}`
    : `&days=${days}`;

  const summaryQ = useQuery({
    queryKey: ['engagement-summary', { project: projectKey, ...range }],
    queryFn: ({ signal }) => apiGet(`/engagement/summary?project=${enc}${rangeQs}`, { signal }),
  });
  const trendQ = useQuery({
    queryKey: ['engagement-timeseries', { project: projectKey, ...range, metric: 'impressions' }],
    queryFn: ({ signal }) => apiGet(`/engagement/timeseries?project=${enc}&metric=impressions${rangeQs}`, { signal }),
  });
  // Followers/profile snapshots stay on a rolling window — the follower
  // trend is account-level, not publication-window-level.
  const accountsQ = useQuery({
    queryKey: ['engagement-accounts', { project: projectKey, days }],
    queryFn: ({ signal }) => apiGet(`/engagement/accounts?project=${enc}&days=${days}`, { signal }),
  });

  // Impressions series grouped by platform (chronological).
  const seriesByPlatform = useMemo(() => {
    const grouped = {};
    for (const pt of trendQ.data?.points || []) {
      (grouped[pt.platform] ||= []).push(pt.value);
    }
    return grouped;
  }, [trendQ.data?.points]);

  // One account per platform for the card header (first/most-recent wins; the
  // accounts endpoint already orders by connected_at DESC within a platform).
  const accountByPlatform = useMemo(() => {
    const map = {};
    for (const a of accountsQ.data?.accounts || []) {
      if (!map[a.platform]) map[a.platform] = a;
    }
    return map;
  }, [accountsQ.data?.accounts]);

  const engByPlatform = useMemo(() => {
    const map = {};
    for (const p of summaryQ.data?.by_platform || []) map[p.platform] = p;
    return map;
  }, [summaryQ.data?.by_platform]);

  if (summaryQ.isLoading || accountsQ.isLoading) {
    return <section className="post-section"><div className="loading">Loading stats…</div></section>;
  }
  if (summaryQ.isError) {
    return <section className="post-section"><div className="error">Couldn’t load stats: {summaryQ.error.message}</div></section>;
  }

  const totals = summaryQ.data?.totals || {};
  const accounts = accountsQ.data?.accounts || [];
  const byPlatform = summaryQ.data?.by_platform || [];

  // Union of every channel worth a card: connected accounts ∪ platforms that
  // have published engagement. Ordered by the catalog, then alphabetically.
  const platformOrder = Object.keys(PLATFORM_META);
  const platforms = Array.from(new Set([
    ...accounts.map((a) => a.platform),
    ...byPlatform.map((p) => p.platform),
  ])).sort((a, b) => {
    const ia = platformOrder.indexOf(a); const ib = platformOrder.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
  });

  const publications = Number(totals.publication_count || 0);
  const noEngagement = publications > 0 && ENG_KEYS.every((k) => Number(totals[k] || 0) === 0);

  return (
    <section className="post-section stats-page">
      <div className="section-h-row">
        <h2>Statistics by social network</h2>
        <div className="stats-range" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={`stats-range-btn${!custom && days === r.days ? ' active' : ''}`}
              aria-pressed={!custom && days === r.days}
              onClick={() => setRange({ days: r.days, from: '', to: '' })}
            >
              {r.label}
            </button>
          ))}
          <label className="filter-date">
            <input type="date" value={range.from} max={range.to || undefined}
              onChange={(e) => setRange({ ...range, from: e.target.value })} />
          </label>
          <label className="filter-date">
            <input type="date" value={range.to} min={range.from || undefined}
              onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </label>
          {custom && (
            <button type="button" className="btn-compact" onClick={() => setRange({ days: 30, from: '', to: '' })}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Nothing connected → point at Connections. */}
      {platforms.length === 0 && (
        <div className="stats-empty">
          <p>No social accounts are connected to this project yet.</p>
          <Link to={`/projects/${projectKey}?tab=connections`} className="btn btn-compact">Go to Connections</Link>
        </div>
      )}

      {platforms.length > 0 && (
        <>
          {/* Project-wide totals over the window. */}
          <p className="stats-strip-label muted small">All connected networks · last {days} days</p>
          <div className="engagement-summary">
            <Tile label={`Posts · ${days}d`} value={fmt(publications)} />
            <Tile label="Impressions" value={fmt(totals.total_impressions)} />
            <Tile label="Reach" value={fmt(totals.total_reach)} />
            <Tile label="Likes" value={fmt(totals.total_likes)} />
            <Tile label="Comments" value={fmt(totals.total_comments)} />
            <Tile label="Shares" value={fmt(totals.total_shares)} />
            <Tile label="Saves" value={fmt(totals.total_saves)} />
          </div>

          {/* Published but zero engagement → platform-side block, not "0 reach". */}
          {noEngagement && (
            <p className="engagement-note">
              {fmt(publications)} post{publications === 1 ? '' : 's'} published in this window, but no engagement stats
              have come in yet. This is usually a platform-side block — Instagram needs the insights permission
              (Meta App Review), or a connected account’s token expired. Check the{' '}
              <Link to={`/projects/${projectKey}?tab=connections`}><b>Connections</b></Link> tab.
            </p>
          )}

          {/* Nothing published yet (but accounts connected) → set expectation. */}
          {publications === 0 && (
            <p className="engagement-note">
              No posts have published in the last {days} days, so there’s no engagement to chart yet. Follower counts
              below refresh automatically every 12 hours; engagement fills in within a few hours of your first post
              going live.
            </p>
          )}

          {/* Per-network cards. */}
          <div className="stats-platform-grid">
            {platforms.map((platform) => (
              <StatCard
                key={platform}
                platform={platform}
                account={accountByPlatform[platform]}
                eng={engByPlatform[platform]}
                impressionsSeries={seriesByPlatform[platform] || []}
                windowDays={days}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function StatCard({ platform, account, eng, impressionsSeries, windowDays }) {
  const name = platformName(platform);
  const icon = platformIcon(platform);

  const followers = account?.followers_count;
  const delta = account?.followers_delta;

  const posts = Number(eng?.publication_count || 0);
  const impressions = Number(eng?.total_impressions || 0);
  const reach = Number(eng?.total_reach || 0);
  const likes = Number(eng?.total_likes || 0);
  const comments = Number(eng?.total_comments || 0);
  const shares = Number(eng?.total_shares || 0);
  const saves = Number(eng?.total_saves || 0);
  const interactions = likes + comments + shares + saves;
  const engRate = impressions > 0 ? (interactions / impressions) * 100 : null;

  const connected = Boolean(account);

  return (
    <div className={`stat-card${connected ? '' : ' stat-card-unlinked'}`}>
      <div className="stat-card-head">
        <span className="stat-card-icon" aria-hidden="true">{icon}</span>
        <div className="stat-card-id">
          <span className={`platform-chip platform-${platform}`}>{name}</span>
          {account?.handle && <span className="stat-card-handle">{account.handle}</span>}
        </div>
        {!connected && <span className="stat-card-status muted small">not connected</span>}
        {connected && account.status !== 'active' && (
          <span className="stat-card-status stat-card-status-warn">{account.status}</span>
        )}
      </div>

      <div className="stat-card-followers">
        <div className="stat-followers-main">
          <span className="stat-followers-val">{followers != null ? fmt(followers) : '—'}</span>
          <span className="stat-followers-lbl">followers</span>
          {delta != null && delta !== 0 && (
            <span className={`stat-delta ${delta > 0 ? 'up' : 'down'}`}>
              {delta > 0 ? '+' : ''}{fmt(delta)}
            </span>
          )}
        </div>
        {account?.series?.length > 1 && (
          <Sparkline values={account.series} width={120} height={26} showDots ariaLabel={`${name} follower trend`} />
        )}
      </div>

      <div className="stat-card-metrics">
        <Metric label="Posts" value={fmt(posts)} />
        <Metric label="Impressions" value={fmt(impressions)} />
        <Metric label="Reach" value={fmt(reach)} />
        <Metric label="Likes" value={fmt(likes)} />
        <Metric label="Comments" value={fmt(comments)} />
        <Metric label="Shares" value={fmt(shares)} />
        <Metric label="Saves" value={fmt(saves)} />
        <Metric label="Eng. rate" value={engRate != null ? `${engRate.toFixed(1)}%` : '—'} />
      </div>

      {impressionsSeries.length > 1 && (
        <div className="stat-card-trend">
          <span className="muted small">Impressions · last {windowDays}d</span>
          <Sparkline values={impressionsSeries} width={220} height={28} ariaLabel={`${name} impressions trend`} />
        </div>
      )}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="summary-tile">
      <div className="summary-tile-label">{label}</div>
      <div className="summary-tile-value">{value}</div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="stat-metric">
      <span className="stat-metric-val">{value}</span>
      <span className="stat-metric-lbl">{label}</span>
    </div>
  );
}
